import { getProjectId, getProjectInfo } from 'store/project';
import { getCurrentUser } from 'store/user';

import { HashMap, LabelledEntity, Identificable } from 'shared/types/commonView';
import { GU } from 'components/common/graphicUploader/types';
import { SubmitOptions } from 'components/dataCreationForms/withInputForm';
import {
  showGenericErrorToast,
  showErrorToastAndLog,
} from 'services/toaster';
import { inspectionDtoToModel } from 'shared/domain/inspection/mapping/toModel';
import { UserModel } from 'shared/domain/user/types/model';
import { userWithPermissionsInDtoToModel } from 'shared/domain/user/mapping/toModel';
import { InspectionInDto } from 'shared/dtos/in/inspection';
import { ProtocolItemInDto } from 'shared/dtos/in/protocolItem';
import { InspectionStatus, ProtocolItemModel } from 'shared/domain/inspection/inspectionModel';
import { ProtocolOutDtoWithChecklistId } from 'shared/dtos/out/inspection';
import { findById } from 'shared/utils/id/findById';
import { getErrorMessage } from 'shared/utils/errorHandling';
import { areObjectIdsEqual, ObjectIdString } from 'helpers/idComparison';
import { debugLog } from 'shared/logger/debugLog';
import {
  toProtocolItems,
  toProtocolSections,
  InspectionFlatForm,
  ProtocolSectionOutDtoWithSectionId,
} from 'views/inspections/wizard/model';
import {
  collectProtocolAnswerSubmitTasks,
  type ProtocolAnswerSubmitTask,
} from 'shared/domain/inspection/mapping/collectProtocolAnswerSubmitTasks';
import { mergeProtocolAnswerUpdatesIntoInspectionInDto } from 'shared/domain/inspection/mapping/mergeProtocolAnswerUpdatesIntoInspectionInDto';
import { ProtocolAnswerInDto } from 'shared/dtos/in/protocolAnswer';
import { hasMissingRequiredFinishProtocolAnswers } from 'views/inspections/wizard/validation';
import {
  filterUnchangedProtocolItems,
  InspectionSubmitFormFields,
  toInspectionOutDto,
} from 'shared/domain/inspection/mapping/toOutDto';
import { PROTOCOL_ITEM_UPLOADER_PREFIX } from '../../constants';
import { PROTOCOL_SECTION_UPLOADER_PREFIX } from 'components/inspection/useInspectionSectionUploaders';
import type { InspectionStoreState } from '../../store';
import type {
  FinishInspectionIfRequestedProps,
  InspectionApiActions,
  InspectionSubmitResponse,
  SubmitInspectionProps,
  SubmitFlowInput,
  SubmitPreparedPayload,
  UpdateInspectionFromResponseProps,
} from './types';
import type { StateCreator } from 'zustand';

const FINISH_VALIDATION_ERROR_CODE = 'INSPECTION_FINISH_VALIDATION_FAILED';
const PROTOCOL_ITEM_NOT_FOUND_FOR_ANSWER =
  'INSPECTION_PROTOCOL_ITEM_NOT_FOUND_FOR_ANSWER';
const PROTOCOL_ANSWER_STUB_NOT_FOUND = 'INSPECTION_PROTOCOL_ANSWER_STUB_NOT_FOUND';

function failProtocolAnswerSubmit(
  logMessage: string,
  errorCode: string,
  context: Record<string, unknown>
): never {
  debugLog(logMessage, context);
  showErrorToastAndLog(logMessage);
  throw new Error(errorCode);
}

const getDefaultSaveState = () => ({
  isSaving: false,
  onDone: undefined,
  skipRedirectAfterSave: false,
});

function ensureTemplatePresent(
  template: InspectionStoreState['inspectionTemplate']
): asserts template is NonNullable<InspectionStoreState['inspectionTemplate']> {
  if (!template) {
    throw new Error('Template missing');
  }
}

function validateFinishIfNeeded(
  finish: boolean,
  form: InspectionFlatForm,
  inspectionTemplate: InspectionStoreState['inspectionTemplate'],
  protocolItems: ProtocolItemModel[] | undefined
): void {
  if (
    !finish ||
    !inspectionTemplate ||
    !hasMissingRequiredFinishProtocolAnswers(
      inspectionTemplate,
      form,
      protocolItems
    )
  ) {
    return;
  }

  throw new Error(FINISH_VALIDATION_ERROR_CODE);
}

function setSavingState(state: InspectionStoreState): void {
  state.setIsSaving(true);
}

function toInspectionSubmitFormFields(
  form: InspectionFlatForm
): InspectionSubmitFormFields {
  const toId = (v: LabelledEntity): Identificable<ObjectIdString> => ({
    _id: v._id as ObjectIdString,
  });

  return {
    inspectionDate: form.inspectionDate,
    comment: form.comment,
    workTypes: form.workTypes?.map(toId),
    levels: form.levels?.map(toId),
    controlledParty: form.controlledParty?.map(toId),
    contracts: form.contracts?.map(toId),
    sites: form.sites?.map(toId),
  };
}

function prepareSubmitPayload({
  state,
  form,
  finish,
}: SubmitFlowInput): SubmitPreparedPayload {
  const inspectionOutDto = toInspectionOutDto(
    state.inspectionModel,
    toInspectionSubmitFormFields(form)
  );
  const allProtocolItems = toProtocolItems(form);
  const templateSectionById = new Map(
    (state.inspectionTemplate?.sections || []).map((section) => [
      section._id,
      section,
    ])
  );
  const inspectionSectionById = new Map(
    (state.inspectionModel?.sections || []).map((section) => [
      section._id,
      section,
    ])
  );
  const inspectionSectionByOrder = new Map(
    (state.inspectionModel?.sections || []).map((section) => [
      section.order,
      section,
    ])
  );
  const allProtocolSections = toProtocolSections(form).map((section) => {
    const templateSectionId =
      section.templateSectionId || section.sectionId;
    const sectionOrder =
      templateSectionById.get(templateSectionId)?.order;
    const inspectionSection =
      inspectionSectionById.get(section.sectionId) ||
      (sectionOrder != null
        ? inspectionSectionByOrder.get(sectionOrder)
        : undefined);

    return {
      ...section,
      templateSectionId,
      sectionId: inspectionSection?._id || section.sectionId,
      sectionOrder,
      resolvedSectionId: Boolean(inspectionSection),
    };
  });
  const protocolChanges = filterUnchangedProtocolItems(
    state.inspectionModel,
    allProtocolItems
  );

  const sectionChanges = allProtocolSections.filter((section) => {
    const previousSection = state.inspectionModel?.sections?.find(
      (inspectionSection) =>
        inspectionSection._id === section.sectionId ||
        (section.templateSectionId != null &&
          inspectionSection.checklistSectionId ===
            section.templateSectionId) ||
        (section.sectionOrder != null &&
          inspectionSection.order === section.sectionOrder)
    );

    if (!previousSection) {
      return true;
    }

    return (previousSection.comment || '') !== (section.comment || '');
  });

  const protocolAnswerTasks = collectProtocolAnswerSubmitTasks(
    form,
    state.inspectionTemplate!,
    state.inspectionModel?.protocol
  );

  return {
    inspectionOutDto,
    allProtocolItems,
    protocolForSubmit: finish ? allProtocolItems : protocolChanges,
    sectionForSubmit: sectionChanges,
    protocolAnswerTasks,
  };
}

async function uploadFiles(
  uploaders: HashMap<GU>,
  inspectionId: string,
  protocolItemId: string,
  key: string
): Promise<unknown> {
  if (!uploaders[key]) {
    return;
  }

  try {
    await uploaders[key].uploadFiles(
      `/inspection/${inspectionId}/protocolItem/${protocolItemId}/complianceCheck`,
      undefined,
      {
        rethrow: true,
      }
    );

    await Promise.all(uploaders[key].executeChangeset());
  } catch (error) {
    showErrorToastAndLog(`Compliance check document upload failed. Inspection: ${inspectionId}. Protocol item: ${protocolItemId}`,
      error
    );
    throw error;
  }
}

async function uploadSectionFiles(
  uploaders: HashMap<GU>,
  inspectionId: string,
  sectionId: string,
  templateSectionId: string
): Promise<unknown> {
  const key = `${PROTOCOL_SECTION_UPLOADER_PREFIX}-${templateSectionId}`;
  if (!uploaders[key]) {
    return;
  }

  try {
    await uploaders[key].uploadFiles(
      `/inspection/${inspectionId}/section/${sectionId}`,
      undefined,
      {
        rethrow: true,
      }
    );

    await Promise.all(uploaders[key].executeChangeset());
  } catch (error) {
    showErrorToastAndLog(`Section document upload failed. Inspection: ${inspectionId}. Section: ${sectionId}`,
      error
    );
    throw error;
  }
}

function resolveProtocolAnswerSubmitTask(
  task: ProtocolAnswerSubmitTask,
  protocolItems: ProtocolItemInDto[]
): {
  protocolItemId: string;
  protocolAnswerId: string;
  body: ProtocolAnswerSubmitTask['body'];
} {
  const protocolItem = protocolItems.find((item) =>
    areObjectIdsEqual(item.templateChecklistItem, task.checklistItemId)
  );

  if (!protocolItem?._id) {
    failProtocolAnswerSubmit(
      'Protocol item not found while saving protocol answer',
      PROTOCOL_ITEM_NOT_FOUND_FOR_ANSWER,
      {
        checklistItemId: task.checklistItemId,
        checklistAnswerId: task.checklistAnswerId,
      }
    );
  }

  const protocolAnswer = protocolItem.answers?.find((answer) =>
    areObjectIdsEqual(answer.checklistAnswerId, task.checklistAnswerId)
  );

  if (!protocolAnswer?._id) {
    failProtocolAnswerSubmit(
      'Protocol answer stub not found while saving protocol answer',
      PROTOCOL_ANSWER_STUB_NOT_FOUND,
      {
        checklistItemId: task.checklistItemId,
        checklistAnswerId: task.checklistAnswerId,
        protocolItemId: protocolItem._id,
      }
    );
  }

  return {
    protocolItemId: protocolItem._id,
    protocolAnswerId: protocolAnswer._id,
    body: task.body,
  };
}

async function putProtocolAnswerTasks(
  protocolAnswerTasks: ProtocolAnswerSubmitTask[],
  inspectionId: string,
  protocolItems: ProtocolItemInDto[],
  inspectionApiActions: InspectionApiActions,
  signal: AbortSignal,
  projectId: string
): Promise<Array<{ protocolItemId: string; answer: ProtocolAnswerInDto }>> {
  const resolvedTasks = protocolAnswerTasks.map((task) =>
    resolveProtocolAnswerSubmitTask(task, protocolItems)
  );

  // Sequential PUTs: stop on first failure so later answers are not sent. Parallel
  // (Promise.all) would leave in-flight requests on the server and partial store
  // updates while the submit aborts; Promise.allSettled does not fix server-side
  // partial persistence or non-idempotent retry risk.
  const updates: Array<{ protocolItemId: string; answer: ProtocolAnswerInDto }> =
    [];

  for (const { protocolItemId, protocolAnswerId, body } of resolvedTasks) {
    try {
      const updatedAnswer = await inspectionApiActions.putProtocolAnswer(
        inspectionId,
        protocolItemId,
        protocolAnswerId,
        body,
        signal,
        projectId
      );

      updates.push({
        protocolItemId,
        answer: updatedAnswer,
      });
    } catch (error) {
      showErrorToastAndLog('Failed to save protocol answer',
        error
      );
      throw error;
    }
  }

  return updates;
}

function putProtocols(
  filteredProtocolItems: ProtocolOutDtoWithChecklistId[],
  filteredProtocolSections: ProtocolSectionOutDtoWithSectionId[],
  allProtocolItems: ProtocolOutDtoWithChecklistId[],
  protocolAnswerTasks: ProtocolAnswerSubmitTask[],
  signal: AbortSignal,
  uploaders: HashMap<GU>,
  projectId: string,
  isNewInspection: boolean,
  inspectionApiActions: InspectionApiActions
): (response: InspectionInDto) => Promise<InspectionSubmitResponse> {
  return async (response): Promise<InspectionSubmitResponse> => {
    const inspectionId = response._id;
    let inspection = response;
    const filePromises: Promise<unknown>[] = [];

    let protocols: ProtocolItemInDto[] = [];
    const updatedSectionComments: Array<{
      sectionId: string;
      templateSectionId?: string;
      sectionOrder?: string;
      comment: string;
    }> = [];
    try {
      for (const protocolItem of filteredProtocolItems) {
        const matchingProtocol = response.protocol.find((item) =>
          areObjectIdsEqual(item.templateChecklistItem, protocolItem.checklistId)
        );

        if (!matchingProtocol) {
          continue;
        }

        const comment = protocolItem.complianceCheck.comment;
        const protocolItemBody = {
          comment,
        };

        const savedProtocol = await inspectionApiActions.putProtocol(
          inspectionId,
          matchingProtocol._id,
          protocolItemBody,
          signal,
          projectId
        );

        protocols.push(savedProtocol);
      }
    } catch (error) {
      showErrorToastAndLog('Error while submitting inspection',
        error
      );
      throw error;
    }

    const protocolAnswerUpdates = await putProtocolAnswerTasks(
      protocolAnswerTasks,
      inspectionId,
      inspection.protocol,
      inspectionApiActions,
      signal,
      projectId
    );

    if (protocolAnswerUpdates.length > 0) {
      inspection = mergeProtocolAnswerUpdatesIntoInspectionInDto(
        inspection,
        protocolAnswerUpdates
      );
      protocols = protocols.map((protocolItem) => {
        const updatedProtocol = inspection.protocol.find(
          (item) => item._id === protocolItem._id
        );
        return updatedProtocol ?? protocolItem;
      });
    }

    try {
      for (const protocolSection of filteredProtocolSections) {
        const matchingSection = response.sections?.find(
          (section) =>
            section._id === protocolSection.sectionId ||
            (protocolSection.templateSectionId != null &&
              section.checklistSectionId ===
                protocolSection.templateSectionId) ||
            (protocolSection.sectionOrder != null &&
              section.order === protocolSection.sectionOrder)
        );
        const targetSectionId =
          matchingSection?._id ||
          (protocolSection.resolvedSectionId
            ? protocolSection.sectionId
            : undefined);

        if (!targetSectionId) {
          debugLog(
            'Skipping protocolSection update due to unresolved section id',
            {
              inspectionId,
              templateSectionId: protocolSection.templateSectionId,
              sectionOrder: protocolSection.sectionOrder,
            }
          );
          continue;
        }

        const savedProtocolSection =
          await inspectionApiActions.putProtocolSection(
          inspectionId,
          targetSectionId,
          { comment: protocolSection.comment },
          projectId
        );

        updatedSectionComments.push({
          sectionId: targetSectionId,
          templateSectionId:
            savedProtocolSection.checklistSectionId ||
            protocolSection.templateSectionId,
          sectionOrder: protocolSection.sectionOrder,
          comment: savedProtocolSection.comment ?? protocolSection.comment,
        });
      }

      if (updatedSectionComments.length > 0) {
        inspection = {
          ...inspection,
          sections: (inspection.sections || []).map((section) => {
            const updatedSection = updatedSectionComments.find(
              (changedSection) =>
                changedSection.sectionId === section._id ||
                (changedSection.templateSectionId != null &&
                  changedSection.templateSectionId ===
                    section.checklistSectionId) ||
                (changedSection.sectionOrder != null &&
                  changedSection.sectionOrder === section.order)
            );

            if (!updatedSection) {
              return section;
            }

            return {
              ...section,
              comment: updatedSection.comment,
            };
          }),
        };
      }
    } catch (error) {
      showErrorToastAndLog('Error while submitting inspection section comments',
        error
      );
      throw error;
    }

    allProtocolItems.forEach((protocolItem) => {
      const matchingProtocol = response.protocol.find((item) =>
        areObjectIdsEqual(item.templateChecklistItem, protocolItem.checklistId)
      );

      if (!matchingProtocol) {
        return;
      }

      filePromises.push(
        uploadFiles(
          uploaders,
          inspectionId,
          matchingProtocol._id,
          `${PROTOCOL_ITEM_UPLOADER_PREFIX}-${matchingProtocol.templateChecklistItem}`
        )
      );
    });

    // Upload section files
    (response.sections || []).forEach((section) => {
      if (!section._id || !section.checklistSectionId) {
        return;
      }

      filePromises.push(
        uploadSectionFiles(
          uploaders,
          inspectionId,
          section._id,
          section.checklistSectionId
        )
      );
    });

    const files = await Promise.all(filePromises);

    return {
      inspection,
      protocols,
      files,
      shouldRedirect: isNewInspection,
    };
  };
}

async function submitInspection({
  body: { inspection, protocol, allProtocolItems },
  sections,
  protocolAnswerTasks,
  signal,
  templateId,
  inspectionId,
  uploaders,

  inspectionApiActions,
}: SubmitInspectionProps): Promise<InspectionSubmitResponse> {
  const projectId = getProjectId();

  if (!inspectionId) {
    if (!templateId) {
      throw new Error(
        'Cannot send inspection. Inpection id and template id is missing.'
      );
    }

    try {
      const postedInspection = await inspectionApiActions.postInspection(
        templateId,
        inspection,
        projectId
      );

      return await putProtocols(
        protocol,
        sections,
        allProtocolItems,
        protocolAnswerTasks,
        signal,
        uploaders,
        projectId,
        true,
        inspectionApiActions
      )(postedInspection);
    } catch (error) {
      showErrorToastAndLog('Error while submitting inspection',
        error
      );
      throw error;
    }
  }

  try {
    const updatedInspection = await inspectionApiActions.putInspection(
      inspectionId,
      inspection,
      signal,
      projectId
    );

    return await putProtocols(
      protocol,
      sections,
      allProtocolItems,
      protocolAnswerTasks,
      signal,
      uploaders,
      projectId,
      false,
      inspectionApiActions
    )(updatedInspection);
  } catch (error) {
    showErrorToastAndLog('Error while updating inspection',
      error
    );
    throw error;
  }
}

function mergeProtocolUpdates(
  inspection: InspectionInDto,
  updatedProtocols?: ProtocolItemInDto[]
): InspectionInDto {
  if (!updatedProtocols?.length) {
    return inspection;
  }

  return {
    ...inspection,
    protocol: inspection.protocol.map((protocolItem) => {
      const updatedProtocol = findById(
        updatedProtocols,
        protocolItem._id,
        'mergeProtocolUpdates'
      );
      return updatedProtocol ?? protocolItem;
    }),
  };
}

async function updateInspectionFromResponse({
  response,
  setInspection,
  setIsSaving,
  users,
  onDone,
}: UpdateInspectionFromResponseProps): Promise<
  void | InspectionSubmitResponse
> {
  setIsSaving(false);

  const mergedInspection = mergeProtocolUpdates(
    response.inspection,
    response.protocols
  );

  const inspectionModel = inspectionDtoToModel(mergedInspection, users);

  setInspection(inspectionModel);

  if (!onDone) {
    return response;
  }

  return await onDone(response);
}

async function finishInspectionIfRequested({
  finish,
  response,
  signal,
  inspectionApiActions,
}: FinishInspectionIfRequestedProps): Promise<void> {
  if (!finish) {
    return;
  }

  const projectId = getProjectId();

  try {
    response.inspection = await inspectionApiActions.putInspection(
      response.inspection._id,
      { status: InspectionStatus.completed },
      signal,
      projectId
    );
  } catch (error) {
    showErrorToastAndLog(
      `Completing inspection id:${response.inspection._id} failed.`,
      error
    );
    throw error;
  }
}

function resolveCurrentUsers(): UserModel[] {
  const currentUser = getCurrentUser();
  const { _id: projectId, organizationId } = getProjectInfo();

  return currentUser
    ? [userWithPermissionsInDtoToModel(currentUser, organizationId, projectId)]
    : [];
}

function handleSubmitFailure(
  state: InspectionStoreState,
  finish: boolean,
  error: unknown
): void {
  const isFinishValidationError =
    finish &&
    Boolean(getErrorMessage(error)?.includes(FINISH_VALIDATION_ERROR_CODE));

  if (isFinishValidationError) {
    showGenericErrorToast();
  }

  state.setIsSaving(false);
}

async function executeInspectionSave(
  state: InspectionStoreState,
  form: InspectionFlatForm,
  uploaders: HashMap<GU> | undefined,
  finish: boolean,
  submitOptions?: SubmitOptions
): Promise<void | InspectionSubmitResponse> {
  const currentTemplate = state.inspectionTemplate;
  ensureTemplatePresent(currentTemplate);

  const inspectionApiActions: InspectionApiActions = {
    postInspection: state.postInspection,
    putInspection: state.putInspection,
    putProtocol: state.putProtocol,
    putProtocolAnswer: state.putProtocolAnswer,
    putProtocolSection: state.putProtocolSection,
  };
  const abortController = new AbortController();

  const {
    inspectionOutDto: preparedInspectionOutDto,
    allProtocolItems,
    protocolForSubmit,
    sectionForSubmit,
    protocolAnswerTasks,
  } =
    prepareSubmitPayload({ state, form, finish });

  const isNewInspection = !state.inspectionModel?._id;
  const inspectionOutDto = {
    ...preparedInspectionOutDto,
    ...(submitOptions?.isCompleted !== undefined
      ? {
          status: submitOptions.isCompleted
            ? InspectionStatus.completed
            : InspectionStatus.inProgress,
        }
      : isNewInspection
        ? { status: InspectionStatus.inProgress }
        : {}),
  };

  validateFinishIfNeeded(
    finish,
    form,
    state.inspectionTemplate,
    state.inspectionModel?.protocol
  );
  setSavingState(state);

  try {
    const response = await submitInspection({
      templateId: currentTemplate._id,
      inspectionId: state.inspectionModel?._id,
      body: {
        inspection: inspectionOutDto,
        protocol: protocolForSubmit,
        allProtocolItems,
      },
      sections: sectionForSubmit,
      protocolAnswerTasks,
      signal: abortController.signal,
      uploaders: uploaders || {},
      inspectionApiActions,
    });

    await finishInspectionIfRequested({
      finish,
      response,
      signal: abortController.signal,
      inspectionApiActions,
    });

    return await updateInspectionFromResponse({
      response,
      setInspection: state.setInspectionModel,
      setIsSaving: state.setIsSaving,
      users: resolveCurrentUsers(),
      onDone: state.onDone,
    });
  } catch (error) {
    handleSubmitFailure(state, finish, error);
    throw error;
  }
}

type InspectionSaveActions = Pick<
  InspectionStoreState,
  | 'isSaving'
  | 'onDone'
  | 'skipRedirectAfterSave'
  | 'setIsSaving'
  | 'saveDraftInspection'
  | 'finishInspection'
  | 'setOnDone'
  | 'setSkipRedirectAfterSave'
  | 'resetInspectionSaving'
>;

type InspectionSaveActionsCreator = StateCreator<
  InspectionStoreState,
  [],
  [],
  InspectionSaveActions
>;

export const createInspectionSaveActions: InspectionSaveActionsCreator = (set, get) => ({
  ...getDefaultSaveState(),
  setIsSaving: (isSaving) => set({ isSaving }),
  saveDraftInspection: async (form, uploaders, options) => {
    const state = get();
    return executeInspectionSave(state, form, uploaders, false, options);
  },
  finishInspection: async (form, uploaders, options) => {
    const state = get();
    return executeInspectionSave(state, form, uploaders, true, options);
  },
  setOnDone: (onDone) => set({ onDone }),
  setSkipRedirectAfterSave: (skipRedirectAfterSave) =>
    set({ skipRedirectAfterSave }),
  resetInspectionSaving: () => set(getDefaultSaveState()),
});